import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import InvoiceApi from './invoiceApi';
import { ItemizedInvoice } from './invoiceType';
import { Alert } from '../components/ui/alert';
import Spinner from '../components/ui/spinner';
import { formatCurrency } from '../lib/global_function';

interface ItemizedInvoicesViewProps {
    invoiceId: string;
}

const ItemizedInvoiceView: React.FC<ItemizedInvoicesViewProps> = ({ invoiceId }) => {
    const [itemizedInvoices, setItemizedInvoices] = useState<ItemizedInvoice[]>([]);
    const [loading, setLoading] = useState(true);
    const [message, setMessage] = useState<string>('');
    const [errorMessage, setErrorMessage] = useState<string>('');
    const navigate = useNavigate();
    const [invoiceTotals, setInvoiceTotals] = useState({
        sub_total: 0,
        tax_rate: 0,
        tax_amount: 0,
        discount_usd: 0,
        amount_paid: 0,
        balance: 0,
    });

    useEffect(() => {
        const fetchItemizedInvoices = async () => {
            try {
                setLoading(true);
                const response = await InvoiceApi.getItemizedInvoices(invoiceId);

                // Extract itemized invoices
                if (response.itemizedInvoices && Array.isArray(response.itemizedInvoices)) {
                    setItemizedInvoices(response.itemizedInvoices);
                } else {
                    setItemizedInvoices([]);
                }

                // Extract totals if available
                if (response.totals) {
                    setInvoiceTotals({
                        sub_total: Number(response.totals.sub_total || 0),
                        tax_rate: Number(response.totals.tax_rate || 0),
                        tax_amount: Number(response.totals.tax_amount || 0),
                        discount_usd: Number(response.totals.discount_usd || 0),
                        amount_paid: Number(response.totals.amount_paid || 0),
                        balance: Number(response.totals.balance || 0),
                    });
                }
            } catch (err) {
                setErrorMessage(err instanceof Error ? err.message : 'An error occurred');
            } finally {
                setLoading(false);
            }
        };
        fetchItemizedInvoices();
    }, [invoiceId]);

    const handleChange = () => {
        if (invoiceId) {
            navigate('/main/project/invoice/itemized_invoice/change', { state: { invoice_id: invoiceId } });
        } else {
            setErrorMessage("Error: update invoice");
        }
    };

    const handleDelete = async (ItemizedinvoiceId: string) => {
        const selectedItemizedInvoice = itemizedInvoices.find(itemizedInvoices => itemizedInvoices.itemized_invoice_id === ItemizedinvoiceId);
    };

    // Convert string/number values to numbers for calculations
    const subtotal = Number(invoiceTotals.sub_total || 0);
    const discountAmount = Number(invoiceTotals.discount_usd || 0);
    const taxAmount = Number(invoiceTotals.tax_amount || 0);
    const taxRate = Number(invoiceTotals.tax_rate || 0);
    const amountPaid = Number(invoiceTotals.amount_paid || 0);
    const balance = Number(invoiceTotals.balance || 0);

    if (loading) {
        return (
            <div className="flex justify-center items-center py-8">
                <Spinner size="md" fullScreen={false} />
            </div>
        );
    }

    return (
        <div className="space-y-4">
            {/* Error Message */}
            {errorMessage && (
                <Alert variant="error">
                    {errorMessage}
                </Alert>
            )}

            {/* Success Message */}
            {message && (
                <Alert variant="success">
                    {message}
                </Alert>
            )}

            {/* Header */}
            <div className="ml-4">
                <span className="font-semibold text-foreground">Itemized Invoices:</span>
            </div>

            {/* No Items Found */}
            {itemizedInvoices.length <= 0 && (
                <div className="ml-12 text-sm text-gray-600">
                    Not found
                </div>
            )}

            {/* Itemized Invoices List */}
            {itemizedInvoices.map((item, index) => (
                <div key={index} className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                    {/* Code */}
                    <div className="mb-2">
                        <span className="font-semibold text-foreground">Code:</span>
                        <span className="ml-8 text-sm text-gray-700">{item.code}</span>
                    </div>

                    {/* Description */}
                    <div className="flex mb-2">
                        <span className="font-semibold text-foreground min-w-[95px]">Description:</span>
                        <div className="ml-8 text-sm text-gray-700 flex-1 break-words whitespace-pre-wrap">
                            {item.description}
                        </div>
                    </div>

                    {/* Amount */}
                    <div className="text-sm">
                        <span className="font-semibold text-foreground">Amount:</span>
                        <span className="ml-2 text-gray-700">${formatCurrency(Number(item.amount_dollar).toFixed(2))}</span>
                    </div>
                </div>
            ))}

            {/* Totals Summary - Show if we have itemized invoices */}
            {itemizedInvoices.length > 0 && (
                <div className="mt-6 p-4 bg-gradient-to-br from-primary/5 to-primary/10  border-2 border-primary/30 rounded-lg shadow-sm">

                    <h4 className="font-semibold text-foreground mb-3 text-base">Invoice Summary</h4>
                    <div className="space-y-2 text-sm">
                        {/* Subtotal */}
                        <div className="flex justify-between">
                            <span className="text-gray-700">Subtotal (Before Tax):</span>
                            <span className="font-semibold text-foreground">
                                ${formatCurrency(subtotal.toFixed(2))}
                            </span>
                        </div>

                        {/* Discount */}
                        {discountAmount > 0 && (
                            <div className="flex justify-between">
                                <span className="text-gray-700">Discount:</span>
                                <span className="font-semibold text-green-600">
                                    -${formatCurrency(discountAmount.toFixed(2))}
                                </span>
                            </div>
                        )}

                        {/* Tax */}
                        {taxAmount > 0 && (
                            <div className="flex justify-between">
                                <span className="text-gray-700">
                                    Tax {taxRate > 0 ? `(${taxRate.toFixed(2)}%)` : ''}:
                                </span>
                                <span className="font-semibold text-foreground">
                                    ${formatCurrency(taxAmount.toFixed(2))}
                                </span>
                            </div>
                        )}

                        {/* Total Amount */}
                        <div className="flex justify-between pt-2 border-t border-gray-300">
                            <span className="font-semibold text-foreground">Total Amount:</span>
                            <span className="font-semibold text-foreground">
                                ${formatCurrency((subtotal - discountAmount + taxAmount).toFixed(2))}
                            </span>
                        </div>

                        {/* Amount Paid */}
                        {amountPaid > 0 && (
                            <div className="flex justify-between">
                                <span className="text-gray-700">Amount Paid:</span>
                                <span className="font-semibold text-green-600">
                                    -${formatCurrency(amountPaid.toFixed(2))}
                                </span>
                            </div>
                        )}

                        {/* Balance Due */}
                        <div className="flex justify-between pt-3 border-t-2 border-gray-400">
                            <span className="font-bold text-foreground text-base">Balance Due:</span>
                            <span className={`font-bold text-lg ${balance > 0 ? 'text-red-600' : 'text-green-600'}`}>
                                ${formatCurrency(balance.toFixed(2))}
                            </span>
                        </div>
                    </div>
                </div>
            )}

            {/* Action buttons at the bottom - Commented out in original */}
            {/* 
            {itemizedInvoices.length > 0 && (
                <div className="flex justify-end">
                    <button onClick={() => handleChange()} className="flex items-center gap-2">
                        <Edit size={16} /> Change Itemized Invoice
                    </button>
                </div>
            )}
            */}
        </div>
    );
};

export default ItemizedInvoiceView;